import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { useNavigate, useLocation, useParams, Navigate, Link } from 'react-router-dom';
import { pickBy } from 'lodash';

import { useWorkspaceContext } from '@/workspace/workspace-context';
import { Account } from '@/workspace/types';

import { accountOnboardingBasePath, shouldRedirectToOnboarding } from '../../onboarding/utils';
import { hasPermission } from './utils';
import { ErrorBanner } from '../../components/banner';
import { AccountContext, prependSlug } from './account-context';

const SelectedAccountStorageKey = 'selectedAccount';

const findAccountBySlug = (slug: string, accounts: Account[]) =>
  accounts.find((account) => account.slug === slug);

const findAccountById = (accountId: string, accounts: Account[]) =>
  accounts.find((account) => account.accountId === accountId);

const getLocalStorageAccountId = () => window.localStorage.getItem(SelectedAccountStorageKey);

const setLocalStorageAccountId = (accountId: string) =>
  window.localStorage.setItem(SelectedAccountStorageKey, accountId);

const replaceSlug = (slug: string) => `/${slug}`;

interface AccountContextProviderProps {
  children: React.ReactNode;
}

export const AccountContextProvider = ({ children }: AccountContextProviderProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { account: selectedAccountSlug } = useParams();
  const { accounts } = useWorkspaceContext();

  useEffect(() => {
    if (accounts === null || accounts.length === 0) {
      return;
    }

    const account =
      findAccountBySlug(selectedAccountSlug ?? '', accounts) ??
      findAccountById(getLocalStorageAccountId() ?? '', accounts) ??
      accounts[0];

    if (account.slug !== selectedAccountSlug) {
      navigate(replaceSlug(account.slug));
    }
  }, [selectedAccountSlug, accounts, navigate, location.pathname]);

  const account = findAccountBySlug(selectedAccountSlug ?? '', accounts) ?? null;

  const enabledFeatures = Object.keys(pickBy(account?.features, Boolean) ?? {}) ?? [];
  const isFeatureEnabled = (feature: string) => enabledFeatures.includes(feature);

  // Account selection side effects
  useEffect(() => {
    if (account === null) {
      return;
    }
    const accountId = account.accountId;
    setLocalStorageAccountId(accountId);

    Sentry.setTags({
      account_id: accountId,
      account_name: account.name,
      account_slug: account.slug,
    });
  }, [account]);

  const switchAccount = (accountId: string) => {
    const account = findAccountById(accountId, accounts);
    if (account === undefined) {
      throw new Error(`Cannot switch account to ${accountId}. Account not found.`);
    }
    navigate(replaceSlug(account.slug));
  };

  if (account === null) {
    return null;
  }

  if (shouldRedirectToOnboarding(account, location.pathname)) {
    return <Navigate to={accountOnboardingBasePath(account)} />;
  }

  const accountData = {
    account,
    accounts,
    enabledFeatures,
    switchAccount,
    hasPermission: (permission: string) => hasPermission(account.me.role, permission),
    isFeatureEnabled,
  };

  return <AccountContext.Provider value={accountData}>{children}</AccountContext.Provider>;
};

export const RedirectToAccount = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { accounts } = useWorkspaceContext();

  useEffect(() => {
    if (accounts === null || accounts.length === 0) {
      return;
    }

    const account = findAccountById(getLocalStorageAccountId() ?? '', accounts) ?? accounts[0];
    navigate(prependSlug(account.slug, `${location.pathname}${location.hash}${location.search}`), {
      replace: true,
    });
  }, [accounts, navigate, location.pathname, location.hash, location.search]);

  if (accounts === null || accounts.length === 0) {
    return (
      <ErrorBanner
        title="No accounts found"
        description={
          <>
            <p>You do not have access to any accounts. Please contact your administrator.</p>
            <p>
              <Link to="/logout">Log out</Link>
            </p>
          </>
        }
      />
    );
  }

  return null;
};
